/**
 * Theme hook — supplier app. See app/customer/src/lib/theme.ts for
 * the canonical version. Storage key namespaced per-app.
 */

import { useEffect, useState } from 'react'

export type ThemeMode = 'light' | 'dark' | 'system'

const STORAGE_KEY = 'angan-greens-supplier-theme'

export function getStoredTheme(): ThemeMode {
  try {
    const v = localStorage.getItem(STORAGE_KEY)
    if (v === 'light' || v === 'dark' || v === 'system') return v
  } catch { /* ignore */ }
  return 'light'
}

function resolveActive(mode: ThemeMode): 'light' | 'dark' {
  // Default is light (see getStoredTheme) and the OS dark setting is
  // NOT silently followed — that was the original complaint. The OS is
  // only honoured when the user *explicitly* picks 'system'. An
  // explicit 'dark'/'light' choice is always respected so the theme
  // toggle is fully functional.
  if (mode === 'system') {
    return typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches
      ? 'dark' : 'light'
  }
  return mode
}

function applyClass(active: 'light' | 'dark') {
  if (typeof document === 'undefined') return
  const root = document.documentElement
  if (active === 'dark') root.classList.add('dark')
  else                   root.classList.remove('dark')
  const meta = document.querySelector('meta[name="theme-color"]')
  if (meta) meta.setAttribute('content', active === 'dark' ? '#0d1410' : '#f8f9f0')
}

if (typeof window !== 'undefined') {
  applyClass(resolveActive(getStoredTheme()))
  window.matchMedia?.('(prefers-color-scheme: dark)').addEventListener?.('change', () => {
    if (getStoredTheme() === 'system') applyClass(resolveActive('system'))
  })
}

export function useTheme() {
  const [mode, setModeState] = useState<ThemeMode>(getStoredTheme)
  const [active, setActive]  = useState<'light' | 'dark'>(() => resolveActive(getStoredTheme()))

  useEffect(() => {
    const next = resolveActive(mode)
    applyClass(next)
    setActive(next)
  }, [mode])

  function setMode(next: ThemeMode) {
    try { localStorage.setItem(STORAGE_KEY, next) } catch { /* ignore */ }
    setModeState(next)
  }

  return { mode, active, setMode }
}
